import React, { useEffect, useState, useRef } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { checkIfAdmin } from "../../../Utils/AuthUtils";
import axios from "axios";
import { BACKEND_DOMAIN } from "../../../common/env";

const LiveBiddingAppBar = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [auction, setAuction] = useState(null);

  const { auctionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [isAdmin, setIsAdmin] = useState(false);

  // Helper function to extract YouTube video ID from URL
  const extractYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Fetch auction details to check if there's a YouTube URL
  useEffect(() => {
    const fetchAuctionDetails = async () => {
      try {
        const response = await axios.get(`${BACKEND_DOMAIN}/auctions/${auctionId}`);
        setAuction(response.data);
        if (response.data.liveAuctionLink) {
          const videoId = extractYouTubeId(response.data.liveAuctionLink);
          setIsVideoLoaded(!!videoId);
        }
      } catch (error) {
        console.error("Error fetching auction details:", error);
      }
    };

    fetchAuctionDetails();
  }, [auctionId]);

      // Check if the user is an admin
      useEffect(() => {
        const verifyAdminStatus = async () => {
          try {
            const isAdminStatus = await checkIfAdmin(auctionId);
            setIsAdmin(isAdminStatus || false); // Ensure it's always a boolean
          } catch (error) {
            console.error("Error verifying admin status:", error);
            setIsAdmin(false); // Default to false for unauthenticated users
          }
        };
        verifyAdminStatus();
      }, [auctionId]);

  const tabs = [
    ...(isAdmin ? [{ label: "Auction Controls", path: `/live-auction/${auctionId}/admin-live-bidding` }] : []),
    // Only show Live Bidding tab if video is loaded
    ...(isVideoLoaded ? [{
      label: "Live Bidding",
      path: isAdmin
        ? `/live-auction/${auctionId}/admin-live-bidding`
        : `/live-auction/${auctionId}/user-live-bidding`,
    }] : []),
    { label: "Teams", path: `/live-auction/${auctionId}/teams` },
    { label: "Players", path: `/live-auction/${auctionId}/players` },
    { label: "Sold Players", path: `/live-auction/${auctionId}/sold-players` },
    { label: "Unsold Players", path: `/live-auction/${auctionId}/unsold-players` },
    { label: "Passed Players", path: `/live-auction/${auctionId}/passed-players` },
    { label: "All Bids", path: `/live-auction/${auctionId}/bids` },
  ];

  // Update active tab on route change
  useEffect(() => {
    const currentPath = location.pathname;
    const matchedTabIndex = tabs.findIndex((tab) => currentPath.includes(tab.path));
    setActiveTab(matchedTabIndex === -1 ? 0 : matchedTabIndex); // Default to 0 if no match is found
  }, [location.pathname, auctionId, isVideoLoaded, isAdmin]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    navigate(tabs[newValue].path); // Navigate to the new tab
  };

  const toggleDrawer = (isOpen) => () => {
    setIsDrawerOpen(isOpen);
  };

  // For mobile view, we'll create a horizontal scrollable tab bar with indicators
  const MobileTabBar = () => {
    const [showLeftIndicator, setShowLeftIndicator] = useState(false);
    const [showRightIndicator, setShowRightIndicator] = useState(true);
    const tabContainerRef = useRef(null);

    const checkScrollPosition = () => {
      if (tabContainerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = tabContainerRef.current;
        setShowLeftIndicator(scrollLeft > 0);
        setShowRightIndicator(scrollLeft < scrollWidth - clientWidth - 1);
      }
    };

    const scrollTabs = (direction) => {
      if (tabContainerRef.current) {
        const scrollAmount = 150;
        const newScrollLeft = direction === 'left' 
          ? tabContainerRef.current.scrollLeft - scrollAmount
          : tabContainerRef.current.scrollLeft + scrollAmount;
        
        tabContainerRef.current.scrollTo({
          left: newScrollLeft,
          behavior: 'smooth'
        });
      }
    };

    useEffect(() => {
      checkScrollPosition();
      const container = tabContainerRef.current;
      if (container) {
        container.addEventListener('scroll', checkScrollPosition);
        return () => container.removeEventListener('scroll', checkScrollPosition);
      }
    }, []);

    return (
      <Box sx={{ 
        display: 'flex',
        width: '100%',
        position: 'relative'
      }}>
        {/* Left scroll indicator */}
        {showLeftIndicator && (
          <Box 
            onClick={() => scrollTabs('left')}
            sx={{ 
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '40px',
              background: 'linear-gradient(90deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 70%, rgba(255,255,255,0) 100%)',
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                background: 'linear-gradient(90deg, rgba(243,244,246,0.95) 0%, rgba(243,244,246,0.8) 70%, rgba(243,244,246,0) 100%)',
              }
            }}
          >
            <Box sx={{ 
              color: '#4b5563', 
              fontSize: '1.5rem',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.7)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.9)',
                transform: 'scale(1.1)',
              }
            }}>
              ‹
            </Box>
          </Box>
        )}

        <Box 
          ref={tabContainerRef}
          sx={{ 
            display: 'flex',
            overflowX: 'auto',
            width: '100%',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            padding: '0 5px',
            position: 'relative',
            scrollBehavior: 'smooth'
          }}
        >
          {tabs.map((tab, index) => (
            <Box
              key={index}
              component={Link}
              to={tab.path}
              onClick={() => handleTabChange(null, index)}
              sx={{
                flex: '0 0 auto',
                textAlign: "center",
                padding: { xs: "6px 10px", sm: "8px 12px" },
                fontWeight: activeTab === index ? "600" : "500",
                color: activeTab === index ? "#1f2937" : "#64748b",
                backgroundColor: activeTab === index ? "#f3f4f6" : "transparent",
                borderRight: "1px solid #e5e7eb",
                textDecoration: "none",
                cursor: "pointer",
                whiteSpace: "nowrap",
                fontSize: { xs: "0.8rem", sm: "0.85rem" },
                "&:hover": {
                  backgroundColor: "#f9fafb",
                  color: "#374151",
                },
                // Add active indicator
                position: 'relative',
                '&::after': {
                  content: activeTab === index ? '""' : 'none',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: { xs: '2px', sm: '3px' },
                  backgroundColor: '#3b82f6',
                }
              }}
            >
              {tab.label}
            </Box>
          ))}
        </Box>

        {/* Right scroll indicator */}
        {showRightIndicator && (
          <Box 
            onClick={() => scrollTabs('right')}
            sx={{ 
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: '40px',
              background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.7) 30%, rgba(255,255,255,0.9) 100%)',
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                background: 'linear-gradient(90deg, rgba(243,244,246,0) 0%, rgba(243,244,246,0.8) 30%, rgba(243,244,246,0.95) 100%)',
              }
            }}
          >
            <Box sx={{ 
              color: '#4b5563', 
              fontSize: '1.5rem',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.7)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.9)',
                transform: 'scale(1.1)',
              }
            }}>
              ›
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    // Table-like header design with width aligned to content below
    <AppBar
      position="static"
      sx={{
        zIndex: 1000,
        background: "#ffffff",
        border: "1px solid #d1d5db",
        borderRadius: "0",
        boxShadow: "none",
        marginBottom: "0",
        width: "100%", // Full width
        maxWidth: "1200px", // Match the max width of content below
        margin: "20px auto 0", // Center and add top margin
      }}
    >
      <Toolbar
        sx={{
          minHeight: "40px",
          padding: "0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {/* Hamburger Menu for Small Screens */}
        <Box sx={{ display: { xs: "block", md: "none" }, width: '100%' }}>
          {/* Show horizontal scrollable tabs for mobile instead of hamburger menu */}
          <MobileTabBar />
        </Box>

        {/* Table-like Columns for Medium and Larger Screens */}
        <Box sx={{ 
          display: { xs: "none", md: "flex" }, 
          width: "100%",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 10px"
        }}>
          {tabs.map((tab, index) => (
            <Box
              key={index}
              component={Link}
              to={tab.path}
              onClick={() => handleTabChange(null, index)}
              sx={{
                flex: 1,
                textAlign: "center",
                padding: "10px 0",
                fontWeight: activeTab === index ? "600" : "500",
                color: activeTab === index ? "#1f2937" : "#64748b",
                backgroundColor: activeTab === index ? "#f3f4f6" : "transparent",
                borderRight: index < tabs.length - 1 ? "1px solid #e5e7eb" : "none",
                textDecoration: "none",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "#f9fafb",
                  color: "#374151",
                },
              }}
            >
              {tab.label}
            </Box>
          ))}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default LiveBiddingAppBar;